import React, { useEffect, useState } from 'react';
import { Table, Card, Typography, Spin, message, Select, Button } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { DownloadOutlined } from '@ant-design/icons';
import apiService from '../../services/api';
import { useChannels } from '../../hooks/useChannels';
import { COLOR_PRIMARY } from '../../utils/colors';
import dayjs from 'dayjs';
import DateRangeFilter from '../../components/DateRangeFilter';

const { Title } = Typography;
const { Option } = Select;

const PAGE_SIZE = 20;

function toCSV(rows, columns) {
  const header = columns.map(col => col.title).join(',');
  const csvRows = rows.map(row =>
    columns.map(col => {
      if (col.key === 'reference') return '"' + (row.reference || '').replace(/"/g, '""') + '"';
      if (col.key === 'job') return '"' + (row.job?.bookingReference || '') + '"';
      if (col.key === 'channel') return '"' + (row.job?.channel || '') + '"';
      if (col.key === 'dueDate') return '"' + (row.dueDate ? dayjs(row.dueDate).format('YYYY-MM-DD') : '') + '"';
      if (col.key === 'customer') return '"' + (row.job?.customer?.name || '') + '"';
      let val = typeof col.render === 'function' ? (col.dataIndex ? row[col.dataIndex] : '') : row[col.dataIndex];
      if (typeof val === 'string') val = '"' + val.replace(/"/g, '""') + '"';
      return val;
    }).join(',')
  );
  return [header, ...csvRows].join('\n');
}

const OverduePayments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const { channels } = useChannels();
  const [selectedChannel, setSelectedChannel] = useState();
  const [plPosition, setPlPosition] = useState();
  const [category, setCategory] = useState();
  const [categories, setCategories] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [dateFilter, setDateFilter] = useState({ year: dayjs().year() });
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState();
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch unique customers for filter
    const fetchCustomers = async () => {
      try {
        const res = await apiService.customers.getUnique();
        setCustomers(res.data || []);
      } catch (err) {
        message.error('Failed to fetch customers');
      }
    };
    fetchCustomers();
  }, []);

  useEffect(() => {
    const fetchOverduePayments = async () => {
      setLoading(true);
      try {
        const params = { status: 'overdue', dateField: 'dueDate' };
        if (selectedChannel) params.channel = selectedChannel;
        if (plPosition) params.plPosition = plPosition;
        if (category) params.category = category;
        if (selectedCustomer) params.customer = selectedCustomer;
        if (dateFilter.dateRange && dateFilter.dateRange.length === 2) {
          params.startDate = dateFilter.dateRange[0].format('YYYY-MM-DD');
          params.endDate = dateFilter.dateRange[1].format('YYYY-MM-DD');
        } else if (dateFilter.year !== undefined) {
          params.year = dateFilter.year;
          if (dateFilter.month !== undefined) params.month = dateFilter.month;
        }
        const res = await apiService.payments.getAll(params);
        let data = res.data.data || res.data || [];

        setPayments(data);
        // Extract unique categories from the data for the filter
        const uniqueCategories = Array.from(new Set(data.map(p => p.category).filter(Boolean)));
        setCategories(uniqueCategories);
        setCurrentPage(1); // Reset to first page on filter change
      } catch (err) {
        message.error('Failed to fetch overdue payments');
      } finally {
        setLoading(false);
      }
    };
    fetchOverduePayments();
    // eslint-disable-next-line
  }, [selectedChannel, plPosition, category, dateFilter, selectedCustomer]);

  const columns = [
    { 
      title: 'Reference', 
      dataIndex: 'reference', 
      key: 'reference',
      render: (text, record) => (
        <Link to={`/payments/${record._id}`} style={{ color: COLOR_PRIMARY, textDecoration: 'underline' }}>{text}</Link>
      )
    },
    { title: 'Amount', dataIndex: 'totalAmount', key: 'totalAmount', render: val => `£${val?.toFixed(2)}` },
    { title: 'Due Date', dataIndex: 'dueDate', key: 'dueDate', render: val => val ? dayjs(val).format('YYYY-MM-DD') : '' },
    // Removed Status column
    { title: 'PL Position', dataIndex: 'plPosition', key: 'plPosition' },
    { title: 'Category', dataIndex: 'category', key: 'category' },
    {
      title: 'Job',
      key: 'job',
      render: (_, record) =>
        record.job && record.job._id ? (
          <Link to={`/jobs/${record.job._id}`} style={{ color: COLOR_PRIMARY, textDecoration: 'underline' }} onClick={e => e.stopPropagation()}>
            {record.job.bookingReference || ''}
          </Link>
        ) : (
          record.job?.bookingReference || ''
        ),
    },
    { title: 'Channel', key: 'channel', render: (_, record) => record.job?.channel || '' },
    { title: 'Customer', key: 'customer', render: (_, record) => record.job?.customer?.name || '' },
  ];

  const total = payments.length;
  const startIdx = (currentPage - 1) * PAGE_SIZE + 1;
  const endIdx = Math.min(currentPage * PAGE_SIZE, total);
  const totalOverdue = payments.reduce((sum, p) => sum + (p.totalAmount || 0), 0);

  const handleExport = () => {
    const csv = toCSV(payments, columns);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'overdue_payments.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Card style={{ maxWidth: 1100, margin: 'auto', marginTop: 32 }}>
      <Title level={2}>Overdue Payments</Title>
      <div style={{ marginBottom: 16, display: 'flex', gap: 16, alignItems: 'center' }}>
        <Select
          allowClear
          showSearch
          style={{ width: 240 }}
          placeholder="Filter by channel"
          value={selectedChannel}
          onChange={setSelectedChannel}
          optionFilterProp="children"
        >
          {channels.map(channel => (
            <Option key={channel._id || channel.name} value={channel.name}>{channel.name}</Option>
          ))}
        </Select>
        <Select
          allowClear
          style={{ width: 180 }}
          placeholder="Filter by PL Position"
          value={plPosition}
          onChange={setPlPosition}
        >
          <Option value="income">Income</Option>
          <Option value="expense">Expense</Option>
        </Select>
        <Select
          allowClear
          style={{ width: 180 }}
          placeholder="Filter by Category"
          value={category}
          onChange={setCategory}
        >
          {categories.map(cat => (
            <Option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</Option>
          ))}
        </Select>
        <Select
          allowClear
          showSearch
          style={{ width: 220 }}
          placeholder="Filter by customer"
          value={selectedCustomer}
          onChange={setSelectedCustomer}
          optionFilterProp="children"
          filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
        >
          {customers.map(customer => (
            <Option key={customer.name} value={customer.name}>{customer.name}</Option>
          ))}
        </Select>
        <DateRangeFilter onChange={setDateFilter} />
        <Button icon={<DownloadOutlined />} onClick={handleExport} type="primary">
          Export CSV
        </Button>
      </div>
      <div style={{ marginBottom: 8, fontWeight: 500, color: '#cf1322', fontSize: 16 }}>
        Total Overdue: £{totalOverdue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </div>
      {loading ? <Spin size="large" /> : (
        <>
          <Table
            columns={columns}
            dataSource={payments}
            rowKey={record => record._id}
            pagination={{ pageSize: PAGE_SIZE, current: currentPage, onChange: setCurrentPage }}
            onRow={record => ({
              onClick: () => navigate(`/payments/${record._id}`),
              style: { cursor: 'pointer' },
            })}
          />
          <div style={{ marginTop: 8, textAlign: 'right', fontWeight: 500 }}>
            {total > 0 ? `Showing ${startIdx}-${endIdx} of ${total}` : 'No records found'}
          </div>
        </>
      )}
    </Card>
  );
};

export default OverduePayments; 